import { parserOptions } from "@insite/client-framework/Common/BasicSelectors";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import { HasCategoryContext, withCategory } from "@insite/client-framework/Components/CategoryContext";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import translate from "@insite/client-framework/Translate";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import Clickable, { ClickablePresentationProps } from "@insite/mobius/Clickable";
import { BaseTheme } from "@insite/mobius/globals/baseTheme";
import GridContainer, { GridContainerProps } from "@insite/mobius/GridContainer";
import GridItem, { GridItemProps } from "@insite/mobius/GridItem";
import LazyImage, { LazyImageProps } from "@insite/mobius/LazyImage";
import Link, { LinkPresentationProps } from "@insite/mobius/Link";
import Typography from "@insite/mobius/Typography";
import breakpointMediaQueries from "@insite/mobius/utilities/breakpointMediaQueries";
import getColor from "@insite/mobius/utilities/getColor";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import CustomApplicationState from "@tecumseh/Store/Data/CustomApplicationState/CustomApplicationState";
import { COLORS, TYPOGRAPHY } from "@tecumseh/theme/design";
import parse from "html-react-parser";
import * as React from "react";
import { connect } from "react-redux";
import { css } from "styled-components";

const enum fields {
    showImages = "showImages",
}

interface Props extends WidgetProps, HasCategoryContext {}

export interface CategoryDetailSubCategoriesStyles {
    container?: GridContainerProps;
    subCategoryItem?: GridItemProps;
    innerContainer?: GridContainerProps;
    subCategoryImageItem?: GridItemProps;
    subCategoryImageWrapper?: InjectableCss;
    brandSubCategoryImageWrapper?: InjectableCss;
    subCategoryImageClickable?: ClickablePresentationProps;
    subCategoryImage?: LazyImageProps;
    subCategoryNameLinkItem?: GridItemProps;
    subCategoryNameLink?: LinkPresentationProps;
    subCategoryShortDescriptionItem?: GridItemProps;
    shortDescription?: InjectableCss;
    subCategoryButtonLinkItem?: GridItemProps;
    button?: LinkPresentationProps;
    circleIcon?: InjectableCss;
    buttonLink?: InjectableCss;
    brandLinkSubMainWrapper?: GridContainerProps;
    subLinkWrapper?: GridItemProps;
    textWrapper?: GridItemProps;
    brandSubCategoryItem?: GridItemProps;
    brandSubCategoryImgItem?: GridItemProps;
    imageLinkWrapper?: InjectableCss;
    brandSubCategoryImage?: LazyImageProps;
    textCircleContainer?: InjectableCss;
}

export const categoryDetailSubCategoriesStyles: CategoryDetailSubCategoriesStyles = {
    container: {
        gap: 0,
        css: css`
            > div[class^="GridOffset"] {
                width: 100%;
                margin: 0 auto;
            }
        `,
    },
    subCategoryItem: {
        width: [12, 12, 6, 6, 6],
        css: css`
            padding: 0 5px 30px;
            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(
                    theme,
                    [
                        css`
                            padding: 0 0 30px;
                        `,
                        css`
                            padding: 0 0 30px;
                        `,
                        css`
                            padding: 0 0 30px;
                        `,
                        null,
                    ],
                    "minMax",
                )}
        `,
    },
    innerContainer: {
        gap: 0,
        css: css`
            > div[class^="GridOffset"] {
                width: 100%;
                margin: 0 auto;
            }
        `,
    },
    subCategoryImageItem: {
        width: [12, 12, 12, 12, 12],
        css: css`
            width: 100%;
            padding: 0 8px 8px 8px;
            min-height: 200px;
            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(
                    theme,
                    [
                        css`
                            min-height: 180px;
                        `,
                        css`
                            min-height: 180px;
                        `,
                        css`
                            min-height: 150px;
                        `,
                        null,
                    ],
                    "minMax",
                )}
        `,
    },
    subCategoryImageWrapper: {
        css: css`
            width: 100%;
        `,
    },
    brandSubCategoryImageWrapper: {
        css: css`
            width: 100%;
            min-height: 220px;
            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(
                    theme,
                    [
                        null,
                        null,
                        css`
                            min-height: 200px;
                        `,
                        null,
                    ],
                    "minMax",
                )}
        `,
    },
    subCategoryImageClickable: {
        css: css`
            width: 100%;
            > div[class^="LazyImageStyle"] {
                opacity: 1;
                background-color: ${COLORS.background.whisper};
                min-height: 200px;
                background-color: rgba(234, 234, 234, 1);
                width: 100%;
                border-radius: 5px;
                position: relative;
                &::after {
                    content: "";
                    position: absolute;
                    width: 49px;
                    bottom: -24px;
                    right: -24px;
                    height: 49px;
                    border: 2px solid transparent;
                    background-color: ${COLORS.background.base};
                    border-image-slice: 10%;
                    transform: rotateZ(45deg);
                }
                ${({ theme }: { theme: BaseTheme }) =>
                    breakpointMediaQueries(
                        theme,
                        [
                            css`
                                min-height: 180px;
                            `,
                            css`
                                min-height: 180px;
                            `,
                            css`
                                min-height: 150px;
                            `,
                            null,
                        ],
                        "minMax",
                    )}
            }
        `,
    },
    subCategoryImage: {
        css: css`
            img {
                height: 100%;
                max-height: 200px;
                object-fit: contain;
                object-position: center top;
            }
        `,
    },
    brandSubCategoryImage: {
        css: css`
            img {
                height: 100%;
                max-height: 220px;
                object-fit: cover;
                object-position: center top;
                ${({ theme }: { theme: BaseTheme }) =>
                    breakpointMediaQueries(
                        theme,
                        [
                            css`
                                min-height: 220px;
                                max-height: unset;
                            `,
                            css`
                                min-height: 220px;
                                max-height: unset;
                            `,
                            null,
                            css`
                                min-height: 220px;
                                max-height: unset;
                            `,
                        ],
                        "minMax",
                    )}
            }
        `,
    },
    subCategoryNameLinkItem: {
        width: [12, 12, 12, 12, 12],
        css: css`
            padding: 0px 8px;
        `,
    },
    subCategoryNameLink: {
        css: css`
            width: 100%;
            text-decoration: none;
        `,
        typographyProps: {
            variant: "h4",
            css: css`
                width: 100%;
                word-wrap: break-word;
                opacity: 1;
                color: ${getColor("secondary")} !important;
                font-family: ${TYPOGRAPHY.fontFamily.base};
                font-weight: ${TYPOGRAPHY.weight.mediumbold};
                font-style: normal;
                letter-spacing: ${TYPOGRAPHY.letterSpacing.small};
                text-align: left;
                &:hover {
                    color: ${getColor("primary")}!important;
                    font-weight: ${TYPOGRAPHY.weight.bold};
                }
            `,
        },
    },
    subCategoryShortDescriptionItem: {
        width: [12, 12, 12, 12, 12],
        css: css`
            padding: 0px 8px;
        `,
    },
    shortDescription: {
        css: css`
            opacity: 1;
            color: ${COLORS.black};
            font-family: ${TYPOGRAPHY.fontFamily.base};
            font-size: ${TYPOGRAPHY.size.small};
            font-weight: ${TYPOGRAPHY.weight.base};
            font-style: normal;
            letter-spacing: ${TYPOGRAPHY.letterSpacing.smallest};
            text-align: left;
            div,
            p,
            ol,
            ul,
            li,
            span,
            section,
            main,
            body {
                opacity: 1;
                color: ${COLORS.black};
                font-family: ${TYPOGRAPHY.fontFamily.base};
                font-size: ${TYPOGRAPHY.size.small};
                font-weight: ${TYPOGRAPHY.weight.base};
                font-style: normal;
                letter-spacing: ${TYPOGRAPHY.letterSpacing.smallest};
            }
        `,
    },
    subCategoryButtonLinkItem: {
        width: [12, 12, 12, 12, 12],
        css: css`
            padding: 0px 8px;
        `,
    },
    button: {
        css: css`
            border-radius: 18px;
            opacity: 1;
            background-color: ${getColor("warning")};
            padding: 5px 20px;
            margin-top: 7px;
            display: flex;
            align-items: center;
            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(
                    theme,
                    [
                        null,
                        null,
                        css`
                            padding: 5px 16px;
                        `,
                        null,
                    ],
                    "minMax",
                )}
            &:hover {
                background-color: ${getColor("warning.contrast")};
            }
            span[class^="TypographyStyle"] {
                opacity: 1;
                color: ${getColor("info.contrast")}!important;
                font-family: ${TYPOGRAPHY.fontFamily.base};
                font-size: ${TYPOGRAPHY.size.smallest};
                font-weight: ${TYPOGRAPHY.weight.baseBold};
                font-style: normal;
                letter-spacing: ${TYPOGRAPHY.letterSpacing.smallest};
                text-align: left;
                ${({ theme }: { theme: BaseTheme }) =>
                    breakpointMediaQueries(
                        theme,
                        [
                            css`
                                font-size: 10px;
                            `,
                            css`
                                font-size: 10px;
                            `,
                            css`
                                font-size: 10px;
                            `,
                            css`
                                font-size: 12px;
                            `,
                        ],
                        "minMax",
                    )}
            }
        `,
    },
    circleIcon: {
        css: css`
            height: 10px;
            width: 10px;
            border-radius: 50%;
            display: inline-block;
            opacity: 1;
            background-color: ${COLORS.background.whiteSmoke};
            border: 0.25px solid rgba(17, 41, 69, 0.219214);
            box-shadow: 0px 0px 2px rgba(0, 0, 0, 0.638631) inset;
            margin-left: 6px;
            float: right;
            /* margin-top: 5px; */
            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(
                    theme,
                    [
                        null,
                        null,
                        css`
                            margin-left: 3px;
                        `,
                        null,
                    ],
                    "minMax",
                )}
        `,
    },
    buttonLink: {
        css: css`
            &:focus {
                outline: none;
            }
            &:focus-visible {
                outline: none;
            }
        `,
    },
    textWrapper: {
        width: 12,
        css: css`
            justify-content: center;
            padding: 5px;
            > a {
                color: ${getColor("primary")};
                font-size: ${TYPOGRAPHY.size.small};
                font-weight: ${TYPOGRAPHY.weight.mediumbold};
                margin-top: 5px;
                &:hover {
                    color: ${getColor("secondary")};
                }
            }
        `,
    },
    subLinkWrapper: {
        width: [12, 12, 12, 12, 12],
        css: css`
            border: 1px solid #8da5c1;
            padding: 0;
            position: relative;
            &::after {
                content: "";
                position: absolute;
                width: 17px;
                right: -6px;
                bottom: -9px;
                height: 22px;
                border-left: 1px solid #8da5c1;
                background-color: white;
                border-image-slice: 10%;
                transform: rotateZ(45deg);
            }
        `,
    },
    brandLinkSubMainWrapper: {
        css: css`
            margin: 0 5px;
            > div[class^="GridOffset"] {
                width: 100%;
                margin: 0 auto;
            }
        `,
    },
    brandSubCategoryItem: {
        width: [12, 12, 4, 3, 3],
        css: css``,
    },
    brandSubCategoryImgItem: {
        width: [12, 12, 12, 12, 12],
        css: css`
            width: 100%;
            padding: 0 2px 2px 2px;
            min-height: 200px;
            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(
                    theme,
                    [
                        css`
                            min-height: 180px;
                        `,
                        css`
                            min-height: 180px;
                        `,
                        css`
                            min-height: 150px;
                        `,
                        null,
                    ],
                    "minMax",
                )}
        `,
    },
    imageLinkWrapper: {
        css: css`
            justify-content: center;
            > div[class^="LazyImageStyle"] {
                width: 100%;
            }
        `,
    },
    textCircleContainer: {
        css: css`
            display: flex;
        `,
    },
};

const styles = categoryDetailSubCategoriesStyles;

const CategoryDetailSubCategories = ({ category, fields: { showImages } }: Props) => {
    if (!category || !category.subCategoryIds || category.subCategoryIds.length === 0) {
        return null;
    }

    return (
        <GridContainer {...styles.container}>
            {category.subCategoryIds.map(subCategoryId => (
                <GridItem
                    key={subCategoryId}
                    {...(category.properties.isFamilyPage === "true"
                        ? styles.brandSubCategoryItem
                        : styles.subCategoryItem)}
                >
                    {category.properties.isFamilyPage === "true" ? (
                        <BrandSubCategoryLink
                            categoryId={subCategoryId}
                            showImages={showImages}
                            parentCategoryId={category.id}
                        />
                    ) : (
                        <SubCategoryLink categoryId={subCategoryId} showImages={showImages} />
                    )}
                </GridItem>
            ))}
        </GridContainer>
    );
};

const BrandSubCategoryLinkView = ({
    category,
    showImages,
    parentCategoryId,
}: ReturnType<typeof mapStateToProps> & { showImages: boolean; parentCategoryId: string }) => {
    if (!category) {
        return null;
    }

    const categoryID = `${category.path}?categoryId=${parentCategoryId}`;
    return (
        <GridContainer {...styles.brandLinkSubMainWrapper} className="brandLinkSubMainWrapper">
            <GridItem {...styles.textWrapper}>
                <Clickable href={categoryID}>{category.shortDescription}</Clickable>
            </GridItem>
            <GridItem {...styles.subLinkWrapper}>
                {showImages && (
                    <GridItem {...styles.brandSubCategoryImgItem}>
                        <StyledWrapper {...styles.brandSubCategoryImageWrapper}>
                            {category.smallImagePath && (
                                <Clickable href={categoryID} {...styles.imageLinkWrapper}>
                                    <LazyImage
                                        src={category.smallImagePath}
                                        altText={category.imageAltText}
                                        {...styles.brandSubCategoryImage}
                                    />
                                </Clickable>
                            )}
                        </StyledWrapper>
                    </GridItem>
                )}
            </GridItem>
        </GridContainer>
    );
};

const SubCategoryLinkView = ({
    category,
    showImages,
}: ReturnType<typeof mapStateToProps> & { showImages: boolean }) => {
    if (!category) {
        return null;
    }
    return (
        <GridContainer {...styles.innerContainer}>
            {showImages && (
                <GridItem {...styles.subCategoryImageItem}>
                    <StyledWrapper {...styles.subCategoryImageWrapper}>
                        {category.smallImagePath && (
                            <Clickable href={category.path} {...styles.subCategoryImageClickable}>
                                <LazyImage
                                    src={category.smallImagePath}
                                    altText={category.imageAltText}
                                    {...styles.subCategoryImage}
                                />
                            </Clickable>
                        )}
                    </StyledWrapper>
                </GridItem>
            )}
            <GridItem {...styles.subCategoryNameLinkItem}>
                <Link
                    href={category.path}
                    {...styles.subCategoryNameLink}
                    data-test-selector={`categoryDetailsSubCategoriesLink_${category.id}`}
                >
                    {category.shortDescription}
                </Link>
            </GridItem>
            {category.properties.description && (
                <GridItem {...styles.subCategoryShortDescriptionItem}>
                    <StyledWrapper {...styles.shortDescription}>
                        {parse(category.properties.description, parserOptions)}
                    </StyledWrapper>
                </GridItem>
            )}
            <GridItem {...styles.subCategoryButtonLinkItem}>
                <StyledWrapper {...styles.button}>
                    {/* <StyledWrapper {...styles.textCircleContainer}> */}
                    <Link href={category.path} {...styles.buttonLink}>
                        {`${translate("Explore")} ${category.shortDescription}`}
                    </Link>
                    <Typography {...styles.circleIcon}></Typography>
                    {/* </StyledWrapper> */}
                </StyledWrapper>
            </GridItem>
        </GridContainer>
    );
};

const getCustomCategoryState = (state: ApplicationState & CustomApplicationState, id: string | undefined) => {
    return id ? state.TECData.customCategoriesById.byId[id] : null;
};

const mapStateToProps = (state: ApplicationState & CustomApplicationState, ownProps: { categoryId: string }) => ({
    // category: getCategoryState(state, ownProps.categoryId).value,
    category: getCustomCategoryState(state, ownProps.categoryId),
});

const SubCategoryLink = connect(mapStateToProps)(SubCategoryLinkView);

const BrandSubCategoryLink = connect(mapStateToProps)(BrandSubCategoryLinkView);

const widgetModule: WidgetModule = {
    component: withCategory(CategoryDetailSubCategories),
    definition: {
        group: "Category Details",
        icon: "list-ul",
        fieldDefinitions: [
            {
                name: fields.showImages,
                displayName: "Show Images",
                editorTemplate: "CheckboxField",
                defaultValue: true,
                fieldType: "General",
                sortOrder: 1,
            },
        ],
        allowedContexts: ["CategoryDetailsPage"],
    },
};

export default widgetModule;
